type ValidatableControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
type ValidationError = {
  code: string;
  message: string;
};
type CustomValidator = (control: ValidatableControl, form: HTMLFormElement) => string | null | undefined;
export type ServerValidationErrors = Record<string, string[] | string | boolean | { message?: string } | undefined | null>;

const controllers = new WeakMap<HTMLFormElement, FormValidationController>();
const customValidators = new Map<string, CustomValidator>();
const nativeValidityCodes = [
  "valueMissing",
  "typeMismatch",
  "patternMismatch",
  "tooLong",
  "tooShort",
  "rangeUnderflow",
  "rangeOverflow",
  "stepMismatch",
  "badInput",
] as const;

function isValidatableControl(element: unknown): element is ValidatableControl {
  return element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement;
}

function isVisible(control: ValidatableControl): boolean {
  return control.getClientRects().length > 0;
}

/**
 * Register a synchronous field rule for `data-validation-rules="rule-name"`.
 * Multiple rule names can be separated by whitespace. HTML error templates
 * can match the returned state with `data-validation-message-when="custom.rule-name"`.
 */
export function registerFormValidator(name: string, validator: CustomValidator): void {
  customValidators.set(name, validator);
}

/**
 * Turn a validation-message login link into the in-page login dropdown when
 * it is present. Falling back to the regular login page keeps the link useful
 * on layouts without the header dropdown.
 */
export function prepareLoginDropdownLink(link: HTMLAnchorElement, referrerUrl = location.href): void {
  link.href = "/login?referrerUrl=" + btoa(referrerUrl);
  link.addEventListener("click", (event) => {
    const loginDropDown = document.querySelector(".login .va-legacy-dropdown") as (HTMLElement & { _x_dataStack?: unknown[] }) | null;
    const context = loginDropDown?._x_dataStack?.[0] as { toggle?: () => void } | undefined;
    if (!loginDropDown || typeof context?.toggle !== "function") {
      return;
    }

    event.preventDefault();
    window.setTimeout(() => {
      context.toggle();
      loginDropDown.scrollIntoView();
      window.setTimeout(() => loginDropDown.querySelector<HTMLInputElement>('input[name="user"]')?.focus());
    });
  });
}

class FormValidationController {
  private readonly remoteErrors = new WeakMap<ValidatableControl, ValidationError>();
  private readonly serverErrors = new WeakMap<ValidatableControl, ValidationError>();
  private readonly serverErrorControls = new Set<ValidatableControl>();
  private readonly remoteRequests = new WeakMap<ValidatableControl, AbortController>();

  constructor(private readonly form: HTMLFormElement) {
    // JavaScript enhances native HTML constraints with accessible, project
    // specific messages. Without JavaScript, the browser's native validation
    // remains available because this property is only set at runtime.
    form.noValidate = true;
    form.addEventListener("focusout", (event) => this.handleFocusOut(event));
    form.addEventListener("change", (event) => this.handleChange(event));
    form.addEventListener("input", (event) => this.handleInput(event));
    form.addEventListener("invalid", (event) => event.preventDefault(), true);
  }

  validate(): boolean {
    const invalidControls = this.controls().filter((control) => !this.validateControl(control));
    if (invalidControls.length) {
      invalidControls[0].focus();
      return false;
    }

    return true;
  }

  async validateAsync(): Promise<boolean> {
    if (!this.validate()) {
      return false;
    }

    await Promise.all(
      this.controls().flatMap((control) => {
        const url = control.dataset.validationRemoteUrl;
        return this.form.dataset.validationRemoteEnabled === "true" && url && control.value.trim()
          ? [this.validateRemote(control, url)]
          : [];
      }),
    );

    return this.validate();
  }

  private allControls(): ValidatableControl[] {
    return Array.from(this.form.elements).filter(isValidatableControl);
  }

  private controls(): ValidatableControl[] {
    return this.allControls().filter(
      (control) => control.willValidate && isVisible(control) && !control.classList.contains("novalidate"),
    );
  }

  private handleFocusOut(event: FocusEvent): void {
    const control = event.target;
    if (!isValidatableControl(control) || !this.form.contains(control)) {
      return;
    }

    if (this.validateControl(control)) {
      this.scheduleRemoteValidation(control);
    }
  }

  private handleChange(event: Event): void {
    const control = event.target;
    if (!isValidatableControl(control) || !this.form.contains(control)) {
      return;
    }

    this.clearRemoteState(control);
    this.clearServerState(control);
    if (this.validateControl(control)) {
      this.scheduleRemoteValidation(control);
    }
  }

  private handleInput(event: Event): void {
    const control = event.target;
    if (!isValidatableControl(control) || !this.form.contains(control)) {
      return;
    }

    // Do not show validation messages while a person is still typing. The
    // field is checked on focusout, change, or submit instead.
    this.clearRemoteState(control);
    this.clearServerState(control);
    this.clearPresentation(control);
    this.clearMatchingDependents(control);
  }

  private validateControl(control: ValidatableControl): boolean {
    if (!control.willValidate || !isVisible(control) || control.classList.contains("novalidate")) {
      return true;
    }

    const error = this.getCustomError(control) ?? this.serverErrors.get(control) ?? this.remoteErrors.get(control) ?? null;
    control.setCustomValidity(error?.message ?? "");

    const validationError = control.validity.valid ? null : error ?? this.getNativeError(control);
    this.render(control, validationError);
    return validationError === null;
  }

  private getCustomError(control: ValidatableControl): ValidationError | null {
    const matchingField = control.dataset.validationMatchField;
    if (matchingField && control.value) {
      const reference = this.findControlByField(matchingField);
      if (reference && reference.value !== control.value) {
        return {
          code: "match",
          message: control.dataset.validationMessageMatch ?? "Die beiden Werte stimmen nicht überein.",
        };
      }
    }

    const ruleNames = (control.dataset.validationRules ?? "").split(/\s+/).filter(Boolean);
    for (const ruleName of ruleNames) {
      const message = customValidators.get(ruleName)?.(control, this.form);
      if (message) {
        return { code: "custom." + ruleName, message };
      }
    }

    return null;
  }

  private getNativeError(control: ValidatableControl): ValidationError {
    const code = nativeValidityCodes.find((key) => control.validity[key]);
    const resolvedCode = code ?? "invalid";

    return {
      code: resolvedCode,
      // Validation copy is deliberately configured through data attributes or
      // templates. The title attribute remains advisory text only.
      message:
        this.getConfiguredMessage(control, resolvedCode) ??
        control.dataset.validationFallbackMessage ??
        "Bitte überprüfe Deine Eingabe.",
    };
  }

  private getConfiguredMessage(control: ValidatableControl, code: string): string | null {
    const dataKey = "validationMessage" + code.charAt(0).toUpperCase() + code.slice(1);
    return control.dataset[dataKey] ?? null;
  }

  private findControlByField(field: string): ValidatableControl | undefined {
    return this.allControls().find((control) => control.dataset.validationField === field);
  }

  private clearMatchingDependents(control: ValidatableControl): void {
    const field = control.dataset.validationField;
    if (!field) {
      return;
    }

    this.allControls()
      .filter((candidate) => candidate.dataset.validationMatchField === field)
      .forEach((candidate) => {
        candidate.setCustomValidity("");
        this.clearPresentation(candidate);
      });
  }

  private findErrorTarget(control: ValidatableControl): HTMLElement | null {
    const field = control.dataset.validationField;
    if (!field) {
      return null;
    }

    return Array.from(this.form.querySelectorAll<HTMLElement>("[data-validation-error-for]")).find(
      (candidate) => candidate.dataset.validationErrorFor === field,
    ) ?? null;
  }

  private clearPresentation(control: ValidatableControl): void {
    control.closest(".input-column")?.classList.remove("error");
    control.removeAttribute("aria-invalid");

    const target = this.findErrorTarget(control);
    if (target) {
      target.replaceChildren();
    }
  }

  private render(control: ValidatableControl, error: ValidationError | null): void {
    control.closest(".input-column")?.classList.toggle("error", Boolean(error));
    if (error) {
      control.setAttribute("aria-invalid", "true");
    } else {
      control.removeAttribute("aria-invalid");
    }

    const target = this.findErrorTarget(control);
    if (!target) {
      return;
    }

    target.setAttribute("aria-live", "polite");
    target.setAttribute("aria-atomic", "true");
    target.replaceChildren();
    if (!error) {
      return;
    }

    // Templates can contain links, emphasis, and translated markup without
    // putting HTML into a data attribute. They are resolved within the
    // enclosing form, so a form may be embedded multiple times on one page.
    const template = Array.from(this.form.querySelectorAll<HTMLTemplateElement>("template[data-validation-message-for]")).find(
      (candidate) =>
        candidate.dataset.validationMessageFor === control.dataset.validationField &&
        candidate.dataset.validationMessageWhen === error.code,
    );
    if (template) {
      const content = template.content.cloneNode(true) as DocumentFragment;
      content.querySelectorAll<HTMLAnchorElement>("[data-validation-login-link]").forEach((link) => {
        const referrerSelector = link.dataset.validationLoginReferrerSelector;
        const referrer = referrerSelector
          ? this.form.querySelector<HTMLInputElement>(referrerSelector)?.value
          : undefined;
        prepareLoginDropdownLink(link, referrer);
      });
      target.append(content);
      return;
    }

    const errorElement = document.createElement("p");
    errorElement.className = "error";
    errorElement.textContent = error.message;
    target.append(errorElement);
  }

  private scheduleRemoteValidation(control: ValidatableControl): void {
    const url = control.dataset.validationRemoteUrl;
    if (this.form.dataset.validationRemoteEnabled !== "true" || !url || !control.value.trim()) {
      return;
    }

    void this.validateRemote(control, url);
  }

  private async validateRemote(control: ValidatableControl, url: string): Promise<void> {
    this.remoteRequests.get(control)?.abort();

    const request = new AbortController();
    this.remoteRequests.set(control, request);
    const value = control.value;
    this.setRemotePending(control, true);

    try {
      const response = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
        body: new URLSearchParams([[control.dataset.validationRemoteParameter ?? control.name, value]]),
        signal: request.signal,
      });
      const result = await response.json();

      if (control.value !== value || request.signal.aborted) {
        return;
      }

      if (result?.found) {
        this.remoteErrors.set(control, {
          code: control.dataset.validationRemoteErrorCode ?? "remote.invalid",
          message: control.dataset.validationRemoteMessage ?? "Diese Eingabe ist nicht verfügbar.",
        });
      } else {
        this.remoteErrors.delete(control);
      }
      this.validateControl(control);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        this.remoteErrors.delete(control);
      }
    } finally {
      if (this.remoteRequests.get(control) === request) {
        this.setRemotePending(control, false);
      }
    }
  }

  private clearRemoteState(control: ValidatableControl): void {
    if (this.remoteErrors.delete(control)) {
      control.setCustomValidity("");
    }
    this.remoteRequests.get(control)?.abort();
    this.setRemotePending(control, false);
  }

  setServerError(field: string, error: ValidationError): boolean {
    const control = this.findControlByField(field);
    if (!control) {
      return false;
    }

    this.serverErrors.set(control, error);
    this.serverErrorControls.add(control);
    control.setCustomValidity(error.message);
    this.render(control, error);
    return true;
  }

  clearServerErrors(): void {
    this.serverErrorControls.forEach((control) => {
      this.serverErrors.delete(control);
      control.setCustomValidity("");
      this.clearPresentation(control);
    });
    this.serverErrorControls.clear();
  }

  private clearServerState(control: ValidatableControl): void {
    if (this.serverErrors.delete(control)) {
      this.serverErrorControls.delete(control);
      control.setCustomValidity("");
    }
  }

  private setRemotePending(control: ValidatableControl, pending: boolean): void {
    const column = control.closest<HTMLElement>(".input-column");
    if (!column) {
      return;
    }

    column.toggleAttribute("aria-busy", pending);
    const legacyLoading = (window as any).varioous?.loading_cl;
    const $ = (window as any).jQuery;
    if (legacyLoading && $) {
      legacyLoading.setSpinner(
        $(column),
        pending,
        true,
        "border-radius: 1rem; position: absolute; top: .45rem; right: .2rem; width: 1.9rem; height: 1.9rem; background-color: #ffffff; background-size: 125%; padding: 0",
      );
    }
  }
}

export function initializeFormValidation(form: HTMLFormElement): FormValidationController {
  let controller = controllers.get(form);
  if (!controller) {
    controller = new FormValidationController(form);
    controllers.set(form, controller);
  }
  return controller;
}

function serverErrorMessage(value: ServerValidationErrors[string]): string | null {
  if (Array.isArray(value)) {
    return serverErrorMessage(value[0]);
  }

  if (typeof value === "string") {
    return value;
  }

  if (value && typeof value === "object" && typeof value.message === "string") {
    return value.message;
  }

  return null;
}

/**
 * Place JSON validation errors next to their corresponding controls. A
 * `template[data-validation-server-error-for]` can map an API-specific error
 * key to another field and provide accessible markup for it.
 */
export function applyServerValidationErrors(form: HTMLFormElement, errors: ServerValidationErrors | undefined): boolean {
  const controller = initializeFormValidation(form);
  controller.clearServerErrors();
  if (!errors) {
    return false;
  }

  const errorsByField = new Map<string, { error: ValidationError; priority: number }>();
  Object.entries(errors).forEach(([errorKey, value]) => {
    if (value === false || value === undefined || value === null) {
      return;
    }

    const template = Array.from(form.querySelectorAll<HTMLTemplateElement>("template[data-validation-server-error-for]")).find(
      (candidate) => candidate.dataset.validationServerErrorFor === errorKey,
    );
    const field = template?.dataset.validationMessageFor ?? errorKey;
    const message = serverErrorMessage(value) ?? "Bitte überprüfe Deine Eingabe.";
    const priority = Number(template?.dataset.validationServerErrorPriority ?? 0);
    const current = errorsByField.get(field);
    if (current && current.priority > priority) {
      return;
    }

    errorsByField.set(field, {
      error: {
        code: template?.dataset.validationMessageWhen ?? "server." + errorKey,
        message,
      },
      priority,
    });
  });

  let applied = false;
  errorsByField.forEach(({ error }, field) => {
    applied = controller.setServerError(field, error) || applied;
  });
  return applied;
}

/** Validate a form from non-Alpine code, for example an invisible reCAPTCHA callback. */
export function validateForm(form: HTMLFormElement): boolean {
  return initializeFormValidation(form).validate();
}

/** Await remote rules before submitting forms controlled by reCAPTCHA callbacks. */
export function validateFormAsync(form: HTMLFormElement): Promise<boolean> {
  return initializeFormValidation(form).validateAsync();
}
